// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Download, Printer } from "lucide-react";
import EmailGate from "@/components/live-sheets/EmailGate";
import { formatCalendarDate } from "@/lib/dates";

const CYAN = "#06B6D4";

const fmtRange = (a: any, b: any) => {
  if (!a) return "";
  const f = (d: any) => formatCalendarDate(d, "MMM d");
  return b && b !== a ? `${f(a)}–${f(b)}` : f(a);
};

const claimBadgeClass = (s: string) => {
  switch ((s || "").toLowerCase()) {
    case "claimed":
      return "bg-green-100 text-green-800";
    case "pending":
      return "bg-amber-100 text-amber-800";
    default:
      return "bg-slate-200 text-slate-700";
  }
};

const COLS = [
  { key: "team_name", label: "Team Name", width: "w-48" },
  { key: "player_name", label: "Player Name", width: "w-48" },
  { key: "grad_year", label: "Grad Year", width: "w-24" },
  { key: "position", label: "Position", width: "w-28" },
  { key: "jersey_number", label: "Jersey #", width: "w-20" },
  { key: "is_guest_player", label: "Guest", width: "w-16", type: "bool" },
  { key: "is_captain", label: "Captain", width: "w-16", type: "bool" },
  { key: "claim_status", label: "Claim Status", width: "w-28", type: "badge" },
];

export default function PlayersSheetPage({ tokenOverride, embedded = false }: { tokenOverride?: string; embedded?: boolean } = {}) {
  const params = useParams<{ token: string }>();
  const token = tokenOverride ?? params.token;
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gatePassed, setGatePassed] = useState(false);

  const [search, setSearch] = useState("");
  const [teamFilter, setTeamFilter] = useState("All");
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const fetchData = useCallback(async () => {
    if (!token) return;
    const { data: result, error: rpcErr } = await supabase.rpc("get_players_sheet_data", { p_token: token });
    if (rpcErr) {
      setError("This link is invalid or has expired.");
      setLoading(false);
      return;
    }
    const parsed = typeof result === "string" ? JSON.parse(result) : result;
    if (parsed?.error) {
      setError("This link is invalid or has expired.");
      setLoading(false);
      return;
    }
    setError(null);
    setData(parsed);
    setLoading(false);
  }, [token]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  const needsGate = useMemo(() => {
    if (!data) return false;
    const allowed = data.allowed_emails;
    if (!Array.isArray(allowed) || allowed.length === 0) return false;
    if (gatePassed) return false;
    const stored = (localStorage.getItem("sheet_viewer_email") || "").toLowerCase();
    if (stored && allowed.map((e) => e.toLowerCase()).includes(stored)) return false;
    return true;
  }, [data, gatePassed]);

  const players = data?.teams || [];

  const teamOptions = useMemo(() => {
    const set = new Set<string>();
    players.forEach((p) => p.team_name && set.add(p.team_name));
    return ["All", ...Array.from(set).sort()];
  }, [players]);

  const filtered = useMemo(() => {
    let rows = [...players];
    const q = search.toLowerCase().trim();
    if (q) {
      rows = rows.filter((r) =>
        [r.team_name, r.player_name].filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
      );
    }
    if (teamFilter !== "All") rows = rows.filter((r) => r.team_name === teamFilter);
    if (sortKey) {
      rows.sort((a, b) => {
        const av = String(a[sortKey] ?? "").toLowerCase();
        const bv = String(b[sortKey] ?? "").toLowerCase();
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return rows;
  }, [players, search, teamFilter, sortKey, sortDir]);

  const summary = useMemo(() => {
    const teams = new Set(players.map((p) => p.team_name).filter(Boolean));
    const claimed = players.filter((p) => (p.claim_status || "").toLowerCase() === "claimed").length;
    return { players: players.length, teams: teams.size, claimed };
  }, [players]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const exportCsv = () => {
    const headers = COLS.map((c) => c.label);
    const lines = [headers.join(",")];
    filtered.forEach((r) => {
      const vals = [
        r.team_name, r.player_name, r.grad_year, r.position, r.jersey_number,
        r.is_guest_player ? "Yes" : "No", r.is_captain ? "Yes" : "No", r.claim_status,
      ];
      lines.push(vals.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `players_${(data?.event?.name || "sheet").replace(/\s+/g, "_")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-slate-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading sheet…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="rounded-lg border bg-white p-8 text-center shadow-sm">
          <p className="text-lg font-semibold text-slate-800">Sheet unavailable</p>
          <p className="mt-1 text-sm text-slate-500">{error || "This link is invalid or has expired."}</p>
        </div>
      </div>
    );
  }

  if (needsGate) {
    return <EmailGate allowedEmails={data.allowed_emails || []} eventName={data.event?.name} onPassed={() => setGatePassed(true)} />;
  }

  const ev = data.event || {};
  const venueLine = [
    ev.venue_name,
    [ev.venue_city, ev.venue_state].filter(Boolean).join(", "),
    fmtRange(ev.start_date, ev.end_date),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className={embedded ? "flex flex-col bg-white" : "h-screen flex flex-col overflow-hidden bg-white"}>
      {!embedded && (
        <div className="px-6 py-4 text-white flex items-center justify-between" style={{ backgroundColor: CYAN }}>
          <div>
            <h1 className="text-xl font-bold leading-tight">{ev.name}</h1>
            <p className="text-sm text-white/90">{venueLine}</p>
          </div>
          <div className="flex items-center gap-3">
            <button className="inline-flex items-center gap-1 rounded bg-white/20 px-3 py-1.5 text-sm font-medium hover:bg-white/30" onClick={exportCsv}>
              <Download className="h-4 w-4" /> CSV
            </button>
            <button className="inline-flex items-center gap-1 rounded bg-white/20 px-3 py-1.5 text-sm font-medium hover:bg-white/30" onClick={() => window.print()}>
              <Printer className="h-4 w-4" /> Print
            </button>
          </div>
        </div>
      )}

      <div className="px-6 py-2 text-sm text-white flex flex-wrap gap-x-6 gap-y-1" style={{ backgroundColor: "rgba(6,182,212,0.9)" }}>
        <span>Players: {summary.players}</span>
        <span>Teams: {summary.teams}</span>
        <span>Claimed: {summary.claimed}</span>
      </div>

      <div className="bg-slate-50 px-6 py-2 flex gap-3 items-center border-b">
        <input
          className="h-9 w-64 rounded-md border border-input bg-background px-3 text-sm"
          placeholder="Search team / player"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)}>
          {teamOptions.map((t) => (
            <option key={t} value={t}>{t === "All" ? "All Teams" : t}</option>
          ))}
        </select>
        <span className="ml-auto text-sm text-slate-500">{filtered.length} of {players.length} players</span>
      </div>

      <div className="flex-1 overflow-auto">
        <table className="border-collapse min-w-max text-sm">
          <thead className="sticky top-0 z-10 bg-slate-100">
            <tr>
              {COLS.map((c) => (
                <th
                  key={c.key}
                  onClick={() => toggleSort(c.key)}
                  className={`${c.width} border px-2 py-2 text-left font-semibold text-slate-700 cursor-pointer select-none hover:bg-slate-200`}
                >
                  {c.label}
                  {sortKey === c.key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => (
              <tr key={r.id} className={i % 2 ? "bg-slate-50/60" : "bg-white"}>
                {COLS.map((c) => {
                  const cls = `${c.width} border px-2 py-1 align-top`;
                  if (c.type === "bool") {
                    return <td key={c.key} className={cls}>{r[c.key] ? "✓" : ""}</td>;
                  }
                  if (c.type === "badge") {
                    return (
                      <td key={c.key} className={cls}>
                        <span className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${claimBadgeClass(r.claim_status)}`}>
                          {r.claim_status || "unclaimed"}
                        </span>
                      </td>
                    );
                  }
                  return <td key={c.key} className={cls}><span className="truncate block">{r[c.key] ?? "—"}</span></td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
